import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Groups as GroupsIcon,
} from "@mui/icons-material";
import type { User, Group } from "@/types/auth";
import { groupService } from "@/services/groupService";

/**
 * Componente para asignar grupos a usuarios
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Todos los componentes con size="small"
 * - dense={true} en listas
 * - Máximo 500 líneas
 */

interface UserGroupsProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onSave: (userId: string, groupIds: string[]) => Promise<void>;
}

export function UserGroups({ open, onClose, user, onSave }: UserGroupsProps) {
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (open && user) {
      loadData();
    }
  }, [open, user]);

  const loadData = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Cargar todos los grupos activos disponibles usando el servicio
      const groups = await groupService.getActiveGroups();
      setAllGroups(groups);

      // Cargar grupos actuales del usuario usando el servicio
      const currentGroups = await groupService.getUserGroups(user.id);
      const groupIds = currentGroups.map((group: Group) => group.id);
      setUserGroups(groupIds);
      setSelectedGroups([...groupIds]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups((prev) => {
      if (prev.includes(groupId)) {
        return prev.filter((id) => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setError(null);

    try {
      await onSave(user.id, selectedGroups);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error guardando cambios");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      // Restaurar selección original al cancelar
      setSelectedGroups([...userGroups]);
      onClose();
    }
  };

  const hasChanges = () => {
    if (selectedGroups.length !== userGroups.length) return true;
    return !selectedGroups.every((id) => userGroups.includes(id));
  };

  const userName =
    user?.nombreCompleto || `${user?.nombre} ${user?.apellido}` || user?.email;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={saving}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1}>
          <GroupsIcon color="primary" />
          <Stack>
            <Typography variant="h6">Asignar Grupos</Typography>
            <Typography variant="body2" color="text.secondary">
              Usuario: {userName}
            </Typography>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Cargando grupos...
              </Typography>
            </Stack>
          ) : (
            <>
              {/* Resumen de cambios */}
              {hasChanges() && (
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Grupos seleccionados:</strong>{" "}
                    {selectedGroups.length}
                    {userGroups.length > 0 && (
                      <>
                        {" | "}
                        <strong>Grupos actuales:</strong> {userGroups.length}
                      </>
                    )}
                  </Typography>
                </Alert>
              )}

              {/* Lista de grupos disponibles */}
              <Typography variant="subtitle1">Grupos Disponibles</Typography>

              {allGroups.length === 0 ? (
                <Alert severity="warning">
                  No hay grupos disponibles para asignar
                </Alert>
              ) : (
                <List dense>
                  {allGroups.map((group, index) => {
                    const isSelected = selectedGroups.includes(group.id);
                    const wasOriginallySelected = userGroups.includes(group.id);

                    return (
                      <React.Fragment key={group.id}>
                        <ListItem
                          onClick={
                            saving
                              ? undefined
                              : () => handleGroupToggle(group.id)
                          }
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1}>
                                <Typography variant="body2">
                                  {group.nombre}
                                </Typography>
                                {group.es_sistema && (
                                  <Chip
                                    label="Sistema"
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                  />
                                )}
                                {wasOriginallySelected && !isSelected && (
                                  <Chip
                                    label="Se eliminará"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                                {!wasOriginallySelected && isSelected && (
                                  <Chip
                                    label="Se agregará"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            }
                            secondary={group.descripcion}
                          />
                          <ListItemSecondaryAction>
                            <Checkbox
                              edge="end"
                              checked={isSelected}
                              onChange={() => handleGroupToggle(group.id)}
                              disabled={saving}
                              size="small"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < allGroups.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}

              {/* Información adicional */}
              <Alert severity="info">
                <Typography variant="body2">
                  Los usuarios heredan automáticamente todos los permisos de los
                  grupos asignados. Los grupos del sistema (como SuperAdmin)
                  tienen permisos especiales.
                </Typography>
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={saving}
          size="small"
          startIcon={<CancelIcon />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading || !hasChanges()}
          variant="contained"
          size="small"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          {saving ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
